import { Component, OnInit, signal } from '@angular/core';
import { from } from 'rxjs';

import { ApiService } from 'src/app/infrastructure/services/api-service/api.service';
import { ButtonToggleComponent } from 'src/app/features/shared/controls/container/button-toggle/button-toggle.component';
import { SlideToggleComponent } from 'src/app/features/shared/controls/container/slide-toggle/slide-toggle.component';
import { autocomplete } from 'src/app/features/shared/controls/models/auto-complete.model';
import { IToggleOption } from 'src/app/features/shared/controls/models/toggle-option.model';
import { injectRequired } from 'src/app/infrastructure/functions/inject-required.function';
import { emailVisibility, TEmailVisibility } from 'src/assets/common/enums/email-visibility.g';
import { IPeerVisibilityDto, peerVisibilityDto } from 'src/assets/common/models/peer-visibility-dto.g';

// Privacy-instellingen van de ingelogde gebruiker (GET/PUT Account/peer-visibility):
//   - ledenkaart: projectgenoten mogen mijn kaart openen (plan gast/vast fase 4); beheerders altijd;
//   - e-mail: wie mijn e-mailadres ziet naast mijzelf en wie mag uitnodigen (plan privacy-contact);
//   - accountfoto als profielfoto in domains waar ik geen eigen profielfoto heb.
// De backend schermt dit per kijker af (PersonContactResultFilter); hier alleen de eigen keuze. Elke wijziging
// stuurt alle drie de waarden; mislukt de PUT, dan blijven de signals ongewijzigd en springt de control terug.
@Component({
    selector: 'app-peer-visibility-setting',
    standalone: true,
    imports: [ButtonToggleComponent, SlideToggleComponent],
    template: `
        <div class="privacy-row">
            <app-slide-toggle
                (valueChange)="emit_change({ visibleToProjectPeers: $event })"
                [disabled]="!loaded$$()"
                [value]="visible$$()"
            />
            <span>{{ visible$$() ? text.card_on : text.card_off }}</span>
        </div>

        <app-button-toggle
            (valueChange)="emit_change({ emailVisibility: $any($event) })"
            [disabled]="!loaded$$()"
            [hint]="text.email_hint"
            [label]="text.email_label"
            [list]="emailOptions"
            [value]="email$$()"
        />

        <div class="privacy-row">
            <app-slide-toggle
                (valueChange)="emit_change({ userPhotoAsProfileFallback: $event })"
                [disabled]="!loaded$$()"
                [value]="photoFallback$$()"
            />
            <span>{{ photoFallback$$() ? text.photo_on : text.photo_off }}</span>
        </div>
    `,
    styles: [`
        :host { display: flex; flex-direction: column; gap: 12px; }
        .privacy-row { display: flex; align-items: center; gap: 12px; }
    `],
})
export class PeerVisibilitySettingComponent implements OnInit {
    private readonly __apiService = injectRequired(ApiService);

    visible$$ = signal<boolean>(false);
    email$$ = signal<TEmailVisibility>(emailVisibility.management);
    photoFallback$$ = signal<boolean>(false);
    loaded$$ = signal<boolean>(false);

    readonly emailOptions: IToggleOption[] = [
        { [autocomplete.itemId]: emailVisibility.management, [autocomplete.itemText]: 'beheer' },
        { [autocomplete.itemId]: emailVisibility.domainMembers, [autocomplete.itemText]: 'domein' },
        { [autocomplete.itemId]: emailVisibility.projectPeers, [autocomplete.itemText]: 'project' },
    ];

    text = {
        card_on: 'projectgenoten kunnen mijn gegevens openen',
        card_off: 'alleen beheerders kunnen mijn gegevens openen',
        // De control zet zelf ':' achter het label.
        email_label: 'mijn email is zichtbaar voor leden',
        email_hint: 'Wie in een domain mag uitnodigen, ziet je e-mailadres altijd.',
        photo_on: 'mijn accountfoto wordt mijn profielfoto in domains zonder eigen profielfoto',
        photo_off: 'mijn accountfoto blijft privé; zonder profielfoto zien anderen geen foto',
    };

    ngOnInit(): void {
        from(this.__apiService.GET('/api/Account/peer-visibility')).subscribe(r => {
            if (!r.data) return;
            this.__apply(r.data as IPeerVisibilityDto);
            this.loaded$$.set(true);
        });
    }

    emit_change(change: Partial<IPeerVisibilityDto>): void {
        const __body: IPeerVisibilityDto = {
            [peerVisibilityDto.visibleToProjectPeers]: this.visible$$(),
            [peerVisibilityDto.emailVisibility]: this.email$$(),
            [peerVisibilityDto.userPhotoAsProfileFallback]: this.photoFallback$$(),
            ...change,
        };
        from(this.__apiService.PUT('/api/Account/peer-visibility', { body: __body as any })).subscribe(r => {
            if (!r.data) return;
            this.__apply(r.data as IPeerVisibilityDto);
        });
    }

    private __apply(dto: IPeerVisibilityDto): void {
        this.visible$$.set(Boolean(dto[peerVisibilityDto.visibleToProjectPeers]));
        this.email$$.set(dto[peerVisibilityDto.emailVisibility] ?? emailVisibility.management);
        this.photoFallback$$.set(Boolean(dto[peerVisibilityDto.userPhotoAsProfileFallback]));
    }
}
